import PowersportsAsset from '../asset/powersports-asset';
import { PageTypes } from '../platform';
import { AssetWrappersLoaded, OlError } from '../events';
import { getDomain } from 'tldts';
import docReady from './doc-ready';

export class OlGtmController {
  private _pushedViewItem = false;
  private readonly _olGtmId: string | undefined;
  private readonly _enableDataLayerEvents: boolean;
  private _prequal_views = 0;
  private _product_views = 0;
  private _eventBus: EventTarget;
  private _waitOnInitialization?: Promise<void>;

  constructor(tagManagerId: string | undefined, eventBus: EventTarget, enableDataLayerEvents = true) {
    this._olGtmId = tagManagerId;
    this._eventBus = eventBus;
    this._enableDataLayerEvents = enableDataLayerEvents;
    docReady(() => {
      this.initializeGTM.bind(this);
    });
  }

  /**
   * Pushes the specified object to the dataLayer, but assures it exists first
   *
   * @param {object} contents - contains what needs to be pushed to the dataLayer
   */

  private async _pushDataLayer(contents: object) {
    if (!this._enableDataLayerEvents) {
      // Partner has opted out of widget dataLayer events (e.g. their GTM reacts to
      // view_item/view_item_list and can enter an infinite request loop).
      return;
    }
    try {
      const now = this._getFormattedTimestamp();
      await this.initializeGTM();
      window.dataLayer = window.dataLayer || [];
      const event: { [key: string]: any } = {
        ...contents,
        time_stamp: now,
        page_location: window.location.href
      };
      if (document.referrer) {
        event.page_referrer = document.referrer;
      }
      const pageDomain = this._getPageDomain();
      if (pageDomain) {
        event.page_domain = pageDomain;
      }
      window.dataLayer.push(event);
    } catch (e) {
      // ignore
    }
  }

  /**
   * Prepares a timestamp in the following format: M/D/YYYY H:mm:ss TZ ("7/28/2022, 3:35:43 EDT")
   *
   * @returns {string} - the formatted timestamp
   */
  private _getFormattedTimestamp() {
    let timestamp = new Date().toLocaleString('en-US', {
      hour12: false,
      timeZoneName: 'short'
    });
    timestamp = timestamp.replace(/,/g, '');
    return timestamp;
  }

  private _getPageDomain() {
    try {
      return getDomain(window.location.href, {
        allowPrivateDomains: true,
        detectIp: false,
        validHosts: ['localhost']
      });
    } catch (e) {
      return null;
    }
  }

  /**
   * Creates an 'item' object for use with 'view_item' and 'view_item_list' dataLayer pushes
   *
   * @param {PowersportsAsset} asset - object that contains item details
   * @param {string} index
   * @returns {object} - assembled 'item'
   */
  private _createItem(asset: PowersportsAsset, index = ''): object {
    const itemObject = {
      item_id: asset.id || '',
      index: index,
      item_brand: asset.make || '',
      item_category: asset.category || '',
      item_variant: asset.color || '',
      item_list_id: '', // This will be an empty string, there is no matching value at this time
      item_list_name: '', // Same as above
      price: asset.price || '',
      quantity: '1',
      item_name: ''
    };
    const year = asset.year || '';
    itemObject.item_name = `${year} ${itemObject.item_brand} ${asset.model || ''}`;
    return itemObject;
  }

  /**
   * Initializes GTM.
   */
  private initializeGTM(): Promise<void> {
    if (!this._olGtmId) {
      return Promise.reject();
    }
    if (this._waitOnInitialization) {
      return this._waitOnInitialization;
    }
    // if it was initialized elsewhere
    if (this.isInitialized()) {
      this._waitOnInitialization = Promise.resolve();
      return this._waitOnInitialization;
    }
    this._waitOnInitialization = new Promise((resolve) => {
      try {
        const headScriptContainer = document.createElement('script');
        headScriptContainer.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer', '${this._olGtmId}');`;
        headScriptContainer.setAttribute('id', 'olGtm');
        document.head.append(headScriptContainer);
        // onload will not fire due to missing `src` attribute, so just set a timer and hope for the best
        setTimeout(resolve, 1000);
      } catch (error) {
        this._eventBus.dispatchEvent(new OlError({ detail: { error, msg: 'Failed to initialize GTM' } }));
      }
    });
    return this._waitOnInitialization;
  }

  /**
   * Pushes a prequal impression to the dataLayer.
   */
  public pushPrequalImpression() {
    this._prequal_views = ++this._prequal_views;
    return this._pushDataLayer({
      event: 'prequal_impression',
      custom_events: {
        prequal_views: this._prequal_views
      }
    });
  }

  private _pushViewItemList(itemList: object[]) {
    return this._pushDataLayer({
      event: 'view_item_list',
      ecommerce: {
        currency: 'USD',
        items: itemList
      }
    });
  }

  private _pushViewItem(itemList: object[]) {
    this._product_views = ++this._product_views;
    return this._pushDataLayer({
      event: 'view_item',
      custom_events: {
        product_views: this._product_views
      },
      ecommerce: {
        currency: 'USD',
        items: itemList
      }
    });
  }

  /**
   * Routes the event to either pushViewItem or pushViewItemList. Adds a listener if the document is not ready.
   *
   * @param {AssetWrappersLoaded} assetWrapperEvent - event that contains AssetWrappers and PageType
   */
  public pushViewItemIntermediary(assetWrapperEvent: AssetWrappersLoaded): Promise<void> {
    if (this._pushedViewItem) return Promise.resolve(); // Do not run if already ran
    if (document.readyState !== 'complete') {
      return new Promise((resolve) => {
        document.addEventListener('DOMContentLoaded', () => {
          this.pushViewItemIntermediary(assetWrapperEvent).finally(resolve);
        });
      });
    }
    const assetWrappers = assetWrapperEvent.detail.assetWrappers;
    const pageType = assetWrapperEvent.detail.pageType;
    if (assetWrappers && assetWrappers.length) {
      this._pushedViewItem = true;
      const itemList = [];
      let index = 0;
      const assets = assetWrappers.map((w) => w['asset']) || [];
      for (const asset of assets) {
        itemList.push(this._createItem(asset as PowersportsAsset, index.toString()));
        index++;
      }
      if (pageType == PageTypes.SRP || pageType == PageTypes.SRP_LEGACY) return this._pushViewItemList(itemList);
      if (pageType == PageTypes.VDP || pageType == PageTypes.VDP_LEGACY || pageType == PageTypes.SUMMARY)
        return this._pushViewItem(itemList);
    }
    // nothing to do here
    return Promise.resolve();
  }

  private isInitialized() {
    if (!this._olGtmId || !window || !window.google_tag_manager) {
      return false;
    }
    // External scripts might have already set up our GTM, detect it here
    return this._olGtmId in window.google_tag_manager;
  }
}
